/**
 * @author <ziad.khoury.hanna@oceandatalab.com>
 * @flow
 */
import {html} from 'htm/preact';
import {Component} from 'preact';
import {useState, useCallback} from 'preact/hooks';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {
  authUser,
  getAuthUser,
  /*:: type APIErrors,
  type User,
  type Seashot,
  type SeashotVisibility, */
} from '../api.js';
import {shallowDiffers, formatDate} from '../utils.js';
import {ExternalLink, ExternalLink2} from './Icons.js';
import {Async} from './Async.js';
import {ErrorView} from './ErrorView.js';
import {SocialButton} from './SocialButton.js';
import {Rating} from './Rating.js';
import {Tags} from './Tags.js';
import {
  SeashotPreview,
  SeashotPreviewThumb,
  SeashotPreviewPlaceholder,
} from './SeashotPreview.js';
import {FlexRow} from './utils.js';

/*:: type Layout = 'tiny' | 'small' | 'medium' | 'full'; */
/*:: type LayoutProps = {+layout: Layout}; */
/*:: type CommonProps = {+component?: string}; */
/*:: type ViewAsyncProps = CommonProps & {
  +seashotId?: string,
  +seashot: Promise<Seashot>,
  +initial: APIErrors | Seashot | null,
  +onLikedChange?: (liked: boolean) => void,
}; */
/*:: type AuthorAsyncProps = {
  +user: Promise<User>,
}; */
/*:: type ViewProps = CommonProps & {
  +seashot: Seashot,
  +onLikedChange?: (liked: boolean) => void,
  +onVisibilityChange?: (visibility: SeashotVisibility) => void,
  +onDeleteClick?: () => void,
}; */
/*:: type PlaceholderProps = CommonProps & {+seashotId?: string}; */
/*:: type ErrorProps = CommonProps & {+error: Error}; */

function SeashotLink({seashotId, ...props} /*: {+seashotId: string} */) {
  const to = {
    pathname: `/s/${seashotId}`,
  };

  return html`<${Link} to=${to} ...${props} />`;
}

function BigOpenLink(props /*: {+href: string} */) {
  return html`
    <a
      title="Open this seashot"
      ...${props}
      className="open -big"
      target="_blank"
      rel="noopener noreferrer"
    >
      <${ExternalLink2} color="#FFF" />
      Open
    </a>
  `;
}

//eslint-disable-next-line no-unused-vars
function OpenLink({seashotId, ...props} /*: {+seashotId: string} */) {
  return html`
    <a
      title="Open this seashot"
      ...${props}
      href=${`/api/seashots/${seashotId}`}
      className="open"
      target="_blank"
      rel="noopener noreferrer"
    >
      <${ExternalLink} color="#FFF" />
    </a>
  `;
}

export class SeashotViewAsync extends Component /*:: <ViewAsyncProps & LayoutProps> */ {
  static propTypes = {
    seashot: PropTypes.instanceOf(Promise).isRequired,
    initial: PropTypes.oneOfType([
      PropTypes.instanceOf(Error),
      PropTypes.object,
    ]),
  };

  shouldComponentUpdate(nextProps /*: ViewAsyncProps & LayoutProps */) /*: boolean */ {
    return shallowDiffers(this.props, nextProps);
  }

  render() /*: React$Node */ {
    const {seashot, initial, ...props} = this.props;

    return html`
      <${Async}
        promise=${seashot}
        initial=${initial}
        then=${(value /*: Seashot */) =>
          html`<${SeashotView} seashot=${value} ...${props} />`}
        catch=${(error /*: Error */) =>
          html`<${SeashotErrorView} error=${error} ...${props} />`}
      >
        <${SeashotViewPlaceholder} ...${props} />
      </${Async}>
    `;
  }
}

export class AuthorAsync extends Component /*:: <AuthorAsyncProps> */ {
  static propTypes = {
    user: PropTypes.instanceOf(Promise).isRequired,
  };

  shouldComponentUpdate(nextProps /*: AuthorAsyncProps */) /*: boolean */ {
    return shallowDiffers(this.props, nextProps);
  }

  render() /*: React$Node */ {
    return html`
      <${Async}
        promise=${this.props.user}
        then=${(user /*: User */) =>
          html`<span className="author">${user.label}</span>`}
        catch=${(error) => {
          console.error(error);
          return html`<span className="author -error">Error</span>`;
        }}
      />
    `;
  }
}

export function SeashotView({
  layout,
  ...props
} /*: ViewProps & LayoutProps */) /*: React$Node */ {
  switch (layout) {
    case 'full':
      return html`<${SeashotViewFull} ...${(props /*: $FlowIssue */)} />`;
    case 'medium':
    case 'small':
      return html`<${SeashotViewMedium} ...${(props /*: $FlowIssue */)} />`;
    case 'tiny':
      return html`<${SeashotViewTiny} ...${(props /*: $FlowIssue */)} />`;
    default:
      return null;
  }
}

export function SeashotViewPlaceholder({
  layout,
  ...props
} /*: PlaceholderProps & LayoutProps */) /*: React$Node */ {
  switch (layout) {
    case 'full':
      return html`<${SeashotViewPlaceholderFull} ...${props} />`;
    case 'medium':
    case 'small':
      return html`<${SeashotViewPlaceholderMedium} ...${props} />`;
    case 'tiny':
      return html`<${SeashotViewPlaceholderTiny} ...${props} />`;
    default:
      return null;
  }
}

export function SeashotErrorView({
  layout,
  ...props
} /*: ErrorProps & LayoutProps */) /*: React$Node */ {
  switch (layout) {
    case 'full':
      return html`<${SeashotErrorViewFull} ...${props} />`;
    case 'medium':
    case 'small':
      return html`<${SeashotErrorViewMedium} ...${props} />`;
    case 'tiny':
      return html`<${SeashotErrorViewTiny} ...${props} />`;
    default:
      return null;
  }
}

async function isAuthUserSeashot(seashot /*: Seashot */) {
  const [user, author] = await Promise.all([getAuthUser(), seashot.author]);
  return author.id === user.id;
}

function AuthorControls({seashot, onVisibilityChange, onDeleteClick}) {
  const editUrl = new URL(seashot.url);
  editUrl.searchParams.append('seashotId', seashot.id);
  editUrl.searchParams.append('seashotAction', 'edit');

  let visibilityControls;

  switch (seashot.visibility) {
    case 'unpublished':
      visibilityControls = html`
        <a href=${editUrl}>Edit</button>
        <button type="button" onClick=${() => onVisibilityChange('private')}>
          Publish as private
        </button>
        <button type="button" onClick=${() => onVisibilityChange('unlisted')}>
          Publish as unlisted
        </button>
        <button type="button" onClick=${() => onVisibilityChange('public')}>
          Publish as public
        </button>
        <button type="button" onClick=${onDeleteClick}>Delete</button>
      `;
      break;
    case 'private':
      visibilityControls = html`
        <a href=${editUrl}>Edit</button>
        <button type="button" onClick=${() => onVisibilityChange('unlisted')}>
          Make unlisted
        </button>
        <button type="button" onClick=${() => onVisibilityChange('public')}>
          Make public
        </button>
        <button type="button" onClick=${onDeleteClick}>Delete</button>
      `;
      break;
    case 'unlisted':
      visibilityControls = html`
        <button type="button" onClick=${() => onVisibilityChange('public')}>
          Make public
        </button>
      `;
      break;
    case 'public':
      visibilityControls = html`
        <button type="button" onClick=${() => onVisibilityChange('unlisted')}>
          Make unlisted
        </button>
      `;
      break;
    default:
      visibilityControls = null;
  }
  return html`<div className="controls">${visibilityControls}</div>`;
}

function Attributions({attributions}) {
  attributions = attributions.trim();
  if (attributions === '') {
    return null;
  }
  const [backgroundName, ...products] = attributions.split('\n');
  const background = html`<p>Background is ${backgroundName}</p>`;
  const productsList =
    products.length > 0
      ? html`
          <p>Products shown:</p>
          <ul>
            ${products.map((p) => html`<li>${p}</li>`)}
          </ul>
        `
      : null;
  return html`<div className="attributions">
    ${background} ${productsList}
  </div>`;
}

export function SeashotViewFull({
  seashot,
  component: Comp = 'div',
  onLikedChange,
  onVisibilityChange,
  onDeleteClick,
} /*: ViewProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot">
      <div className="body">
        <div className="details">
          <${Async}
            promise=${isAuthUserSeashot(seashot)}
            then=${(isTheAuthor /*: boolean */) =>
              (isTheAuthor
                ? html`<${AuthorControls}
                    seashot=${seashot}
                    onVisibilityChange=${onVisibilityChange}
                    onDeleteClick=${onDeleteClick}
                  />`
                : null)}
          />
          <h3 className="title">${seashot.title}</h3>
          <h5 className="subtitle">
            ${'posted on '}
            <time
              className="published-on"
              dateTime=${seashot.createdAt.toISOString()}
            >
              ${formatDate(seashot.createdAt)}
            </time>
            ${' by '}
            <${AuthorAsync} user=${seashot.author} />
          </h5>
          <${Tags}
            tags=${seashot.tags}
            seashotVisibility=${seashot.visibility}
          />
          <${SeashotPreview} seashot=${seashot} playing />
          <div className="controls">
            <${BigOpenLink} href=${seashot.url} />
            ${
              authUser &&
              html`<${Rating}
                likesCount=${seashot.likesCount}
                liked=${seashot.liked}
                onChange=${onLikedChange}
              />`
            }
            <${SocialButton}
              network="twitter"
              text=${seashot.title}
              url=${window.location.href}
            />
            <${SocialButton}
              network="facebook"
              text=${seashot.title}
              url=${window.location.href}
            />
            <${SocialButton}
              network="email"
              text=${seashot.title}
              url=${window.location.href}
              details=${seashot.description}
            />
            ${
              '' /*<SocialButton network="qrcode" text={seashot.title} url={window.location.href} />*/
            }
          </div>
          <${Attributions} attributions=${seashot.attributions} />
          <p className="description">${seashot.description}</p>
        </div>
      </div>
    </${Comp}>`;
}

export function SeashotViewPlaceholderFull({
  seashotId,
  component: Comp = 'div',
} /*: PlaceholderProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot">
      <div className="body">
        <div className="details">
          <h3 className="title" />
          <h5 className="subtitle" />
          <div className="preview" />
          <div className="controls" />
          <p className="attributions" />
          <p className="description" />
        </div>
      </div>
    </${Comp}>`;
}

export function SeashotErrorViewFull({
  error,
  component: Comp = 'div',
} /*: ErrorProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot -error">
      <h1>Oops! 😳</h1>
      <${ErrorView} error=${error} />
    </${Comp}>`;
}

export function SeashotViewMedium({
  seashot,
  component: Comp = 'div',
  onLikedChange,
} /*: ViewProps */) /*: React$Node */ {
  const url = new URL(`/s/${seashot.id}`, window.location).href;

  const [playing, setPlaying] = useState(false);
  const onMouseEnter = useCallback(() => {
    setPlaying(true);
  }, [setPlaying]);
  const onMouseLeave = useCallback(() => {
    setPlaying(false);
  }, [setPlaying]);

  const videoProps =
    seashot.previewType === 'video'
      ? {
          onTouchStart : onMouseEnter,
          onTouchEnd   : onMouseLeave,
          onTouchCancel: onMouseLeave,
          onMouseEnter,
          onMouseLeave,
        }
      : null;

  return html`<${Comp} className="seashot" ...${videoProps}>
      <div className="body">
        <${SeashotPreviewThumb} seashot=${seashot} playing=${playing} />
        <${SeashotLink}
          seashotId=${seashot.id}
          className="link"
          title=${seashot.title}
        />
        <div className="details">
          <div className="title-and-open">
            <h3 className="title">${seashot.title}</h3>
            <${BigOpenLink} href=${seashot.url} />
          </div>
          <h5 className="subtitle">
            ${'posted on '}
            <time
              className="published-on"
              dateTime=${seashot.createdAt.toISOString()}
            >
              ${formatDate(seashot.createdAt)}
            </time>
            ${' by '}
            <${AuthorAsync} user=${seashot.author} />
          </h5>
          <${Tags}
            tags=${seashot.tags}
            seashotVisibility=${seashot.visibility}
          />
          <div className="description">
            <p>${seashot.description}</p>
            <${Attributions} attributions=${seashot.attributions} />
          </div>
        </div>
      </div>
      <div className="controls">
        ${
          authUser &&
          html`<${Rating}
            likesCount=${seashot.likesCount}
            liked=${seashot.liked}
            onChange=${onLikedChange}
          />`
        }
        <${SocialButton} network="twitter" text=${seashot.title} url=${url} />
        <${SocialButton} network="facebook" text=${seashot.title} url=${url} />
        <${SocialButton}
          network="email"
          text=${seashot.title}
          url=${url}
          details=${seashot.description}
        />
        <!-- <${SocialButton} network="qrcode" text=${
    seashot.title
  } url=${url} /> -->
      </div>
    </${Comp}>`;
}

export function SeashotViewPlaceholderMedium({
  seashotId,
  component: Comp = 'div',
} /*: PlaceholderProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot -placeholder">
      <div className="body">
        ${
          seashotId &&
          html`<${SeashotLink} seashotId=${seashotId} className="link" />`
        }
        <${SeashotPreviewPlaceholder} />
        <div className="details">
          <h3 className="title" />
          <h5 className="subtitle" />
          <div className="tags" />
          <p className="description-placeholder">
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </p>
        </div>
      </div>
    </${Comp}>`;
}

export function SeashotErrorViewMedium({
  error,
  component: Comp = 'div',
} /*: ErrorProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot -error">
      <h1>Oops! 😳</h1>
      <${ErrorView} error=${error} />
    </${Comp}>`;
}

export function SeashotViewTiny({
  seashot,
  component: Comp = 'div',
  onLikedChange,
} /*: ViewProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot">
      <div className="body">
        <${SeashotLink}
          seashotId=${seashot.id}
          className="link"
          title=${seashot.title}
        />
        <div className="details">
          <${FlexRow} align="baseline">
            <h3 className="title">${seashot.title}</h3>
          </${FlexRow}>
          <h5 className="subtitle">
            ${'posted on '}
            <time
              className="published-on"
              dateTime=${seashot.createdAt.toISOString()}
            >
              ${formatDate(seashot.createdAt)}
            </time>
            ${' by '}
            <${AuthorAsync} user=${seashot.author} />
          </h5>
        </div>
      </div>
      <div className="controls">
        <${BigOpenLink} href=${seashot.url} />
        ${
          authUser &&
          html`<${Rating}
            likesCount=${seashot.likesCount}
            liked=${seashot.liked}
            onChange=${onLikedChange}
          />`
        }
      </div>
    </${Comp}>`;
}

export function SeashotViewPlaceholderTiny({
  seashotId,
  component: Comp = 'div',
} /*: PlaceholderProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot -placeholder">
      <div className="body">
        ${
          seashotId &&
          html`<${SeashotLink} seashotId=${seashotId} className="link" />`
        }
        <div className="details">
          <${FlexRow} align="baseline">
            <h3 className="title"></h3>
          </${FlexRow}>
          <h5 className="subtitle"></h5>
        </div>
      </div>
    </${Comp}>`;
}

export function SeashotErrorViewTiny({
  error,
  component: Comp = 'div',
} /*: ErrorProps */) /*: React$Node */ {
  return html`<${Comp} className="seashot -error">
      <h1>Oops! 😳</h1>
      <${ErrorView} error=${error} />
    </${Comp}>`;
}
